import { useState, useEffect, useCallback } from "react";
import { FaEye, FaCheck, FaTimes } from "react-icons/fa";

import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import { toast } from "react-toastify";

import ApplyLeave from "./applyleave";
import ViewLeave from "./viewleaves";

import {
  getLeaveRequests,
  approveLeaveRequest,
  rejectLeaveRequest,
  cancelLeaveRequest,
} from "../services/attendanceservice";
import {
  LeaveStatus,
  LeaveStatusFilterOptions,
  getLeaveDayPartLabel,
  getLeaveStatusLabel,
  getLeaveStatusClass,
} from "../../constants/constants";

import api from "../services/api";
import { useAuth } from "../context/authcontext";
import Pagination from "../common/pagination";
import { prefetchEmployees } from "../../utils/employeeCache";

const PAGE_SIZE = 20;

// Pulls the most specific message the backend gave us out of an Axios error.
// DRF errors show up in a few different shapes depending on the endpoint:
//  - { message: "..." }
//  - { detail: "..." }
//  - { remarks: ["This field is required."] }  (field-level validation)
//  - { non_field_errors: ["..."] }
// This tries each in order and falls back to a generic message only if none
// of them are present, so the toast (and console) shows the real reason
// instead of always saying "Failed to X".
const extractErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;

  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  if (data.non_field_errors) return String(data.non_field_errors[0]);

  // First field-level error found, e.g. { remarks: ["This field is required."] }
  const firstFieldKey = Object.keys(data).find((key) => Array.isArray(data[key]) && data[key].length);
  if (firstFieldKey) return `${firstFieldKey}: ${data[firstFieldKey][0]}`;

  return fallback;
};

const Leaves = () => {
  const { hasPermission, hasAnyPermission, hasRole } = useAuth();

  const isAdmin = hasRole("admin");

  const canShowActions =
    isAdmin ||
    hasAnyPermission([
      "leave.view",
      "leave.view_others",
      "leave.approve",
      "leave.reject",
      "leave.cancel",
    ]);

  const canApprove = hasPermission("leave.approve");

  /* =========================================================
     STATE
  ========================================================= */

  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalCount, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [viewLeave, setViewLeave] = useState(null);

  const [approvalModal, setApprovalModal] = useState({
    show: false,
    action: "",
    leaveId: null,
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  /* =========================================================
     FETCH LEAVE REQUESTS
  ========================================================= */

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);

      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await getLeaveRequests(params);

      const data = res.data || res;
      const meta = res.meta || {};

      const rows = Array.isArray(data) ? data : data.results || [];
      setLeaves(rows);
      setTotal(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));

      // Warm the shared employee-detail cache for every employee on this
      // page, in the background, right when the list loads. By the time
      // anyone clicks "View" on a row, department/designation are already
      // cached — ViewLeave renders them immediately instead of showing the
      // loading skeleton and waiting on a fresh fetch.
      const empIds = rows.map((row) => row.employee ?? row.employee_detail?.id);
      prefetchEmployees(empIds);
    } catch {
      toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  /* =========================================================
     INITIAL DATA
  ========================================================= */

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  useEffect(() => {
    api
      .get("dropdowns/employees/")
      .then((r) => setEmployees(r.data.data || r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  /* =========================================================
     APPROVE / REJECT / CANCEL
     Shared by the inline row icons and the ViewLeave popup footer,
     so there's exactly one place that talks to the approval APIs.
  ========================================================= */

  const openApproval = (leaveId, action) => {
    setApprovalModal({ show: true, action, leaveId, remarks: "" });
  };

  const submitApproval = async () => {
    const { action, leaveId, remarks } = approvalModal;

    setSaving(true);

    try {
      if (action === "approve") {
        await approveLeaveRequest(leaveId, {
          status: LeaveStatus.APPROVED,
          remarks: remarks || "Approved",
        });
        toast.success("Leave approved");
      } else if (action === "cancel") {
        await cancelLeaveRequest(leaveId, {
          remarks: remarks || "Cancelled by employee",
        });
        toast.success("Leave cancelled");
      } else {
        await rejectLeaveRequest(leaveId, {
          status: LeaveStatus.REJECTED,
          remarks: remarks || "Rejected",
        });
        toast.success("Leave rejected");
      }

      await fetchLeaves();
      setApprovalModal({ show: false, action: "", leaveId: null, remarks: "" });
      setViewLeave(null);
    } catch (err) {
      // Surface the real backend error instead of a generic message, and log
      // the full response so it's easy to inspect in devtools while debugging.
      // eslint-disable-next-line no-console
      console.error(`Failed to ${action} leave`, err?.response?.data || err);
      toast.error(extractErrorMessage(err, `Failed to ${action}`));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const statusBadge = (leave) => (
    <span className={`emp-status ${getLeaveStatusClass(leave.status)}`}>
      {getLeaveStatusLabel(leave.status)}
    </span>
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="container">
      {/* PAGE HEADER */}
      <div className="page-header">
        <h2>Leave Requests</h2>

        {hasPermission("leave.apply") && (
          <button className="primary-btn" onClick={() => setShowApplyModal(true)}>
            Apply Leave
          </button>
        )}
      </div>

      {/* FILTERS */}
      <div className="filters-card single">
        <input
          className="search-input"
          placeholder="🔍 Search by Employee"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Dropdown
          options={LeaveStatusFilterOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
        />
      </div>

      {/* LEAVE TABLE */}
      {loading ? (
        <div className="empty-state">
          <p>Loading leave requests…</p>
        </div>
      ) : (
        <table className="table leave-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Leave Type</th>
              <th>From</th>
              <th>To</th>
              <th>Day Part</th>
              <th>Status</th>
              {canShowActions && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {leaves.length === 0 ? (
              <tr>
                <td colSpan={canShowActions ? 7 : 6}>
                  <div className="empty-state">
                    <img src="/images/leaves.svg" alt="No Leaves" className="empty-img" />
                    <p>No Leave Requests Found</p>
                  </div>
                </td>
              </tr>
            ) : (
              leaves.map((leave) => (
                <tr key={leave.id} className="leave-record-row">
                  <td className="leave-employee">
                    {leave.employee_detail?.full_name || leave.employee_name || "—"}
                  </td>

                  <td className="leave-type">
                    {leave.leave_type_detail?.name || leave.leave_type_name || "—"}
                  </td>

                  <td className="leave-from">{leave.start_date || "—"}</td>

                  <td className="leave-to">{leave.end_date || "—"}</td>

                  <td className="leave-day-part">{getLeaveDayPartLabel(leave.day_part)}</td>

                  <td className="leave-status-cell">{statusBadge(leave)}</td>

                  {canShowActions && (
                    <td className="action-icons action-icons-cell leave-actions">
                      <button
                        className="icon-btn view"
                        title="View"
                        onClick={() => setViewLeave(leave)}
                      >
                        <FaEye />
                      </button>

                      {leave.status === LeaveStatus.PENDING && (
                        <>
                          {hasPermission("leave.approve") && (
                            <button
                              className="icon-btn view"
                              style={{ color: "#28a745" }}
                              title="Approve Leave"
                              onClick={() => openApproval(leave.id, "approve")}
                            >
                              <FaCheck />
                            </button>
                          )}

                          {hasPermission("leave.reject") && (
                            <button
                              className="icon-btn delete"
                              title="Reject Leave"
                              onClick={() => openApproval(leave.id, "reject")}
                            >
                              <FaTimes />
                            </button>
                          )}
                        </>
                      )}

                      {!canApprove &&
                        hasPermission("leave.cancel") &&
                        leave.can_cancel && (
                          <button
                            className="icon-btn delete"
                            title="Cancel Leave"
                            onClick={() => openApproval(leave.id, "cancel")}
                          >
                            <FaTimes />
                          </button>
                        )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* PAGINATION */}
      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {/* APPLY LEAVE MODAL */}
      {showApplyModal && (
        <ApplyLeave
          employees={employees}
          canApprove={canApprove}
          onClose={() => setShowApplyModal(false)}
          onSaved={() => {
            setShowApplyModal(false);
            fetchLeaves();
          }}
        />
      )}

      {/* VIEW LEAVE POPUP */}
      {viewLeave && (
        <ViewLeave
          leave={viewLeave}
          employees={employees}
          canApprove={canApprove}
          onClose={() => setViewLeave(null)}
          onApprovalAction={openApproval}
        />
      )}

      {/* APPROVAL / REJECT / CANCEL CONFIRM MODAL */}
      {approvalModal.show && (
        <Modal
          title={
            approvalModal.action === "approve"
              ? "Approve Leave"
              : approvalModal.action === "cancel"
              ? "Cancel Leave"
              : "Reject Leave"
          }
          onClose={() =>
            setApprovalModal({ show: false, action: "", leaveId: null, remarks: "" })
          }
        >
          <div className="employee-form" style={{ padding: "20px" }}>
            <p style={{ marginBottom: "12px", color: "#475569" }}>
              Are you sure you want to {approvalModal.action} this leave request? You can
              provide {approvalModal.action === "cancel" ? "a reason" : "optional remarks"}{" "}
              below.
            </p>

            <textarea
              placeholder={
                approvalModal.action === "cancel"
                  ? "Reason for cancellation"
                  : "Manager remarks (optional)"
              }
              value={approvalModal.remarks}
              onChange={(e) =>
                setApprovalModal({ ...approvalModal, remarks: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid var(--border-color)",
                minHeight: "100px",
                background: "var(--bg-main)",
                color: "var(--text-main)",
              }}
            />
          </div>

          <div className="modal-footer">
            <div className="modal-actions">
              <button
                className="primary"
                style={{
                  background:
                    approvalModal.action === "approve"
                      ? "#28a745"
                      : approvalModal.action === "cancel"
                      ? "#f59e0b"
                      : "#dc3545",
                }}
                onClick={submitApproval}
                disabled={saving}
              >
                {saving
                  ? "Processing…"
                  : `Confirm ${
                      approvalModal.action === "approve"
                        ? "Approval"
                        : approvalModal.action === "cancel"
                        ? "Cancellation"
                        : "Rejection"
                    }`}
              </button>

              <button
                className="secondary"
                onClick={() =>
                  setApprovalModal({ show: false, action: "", leaveId: null, remarks: "" })
                }
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Leaves;